import { TONE_OKLCH, type ExpiryTone } from '@frisk/shared';
import { useState } from 'react';
import { AddItem } from '../components/AddItem';
import { IconList, IconPlus, IconTiles } from '../components/icons';
import { ItemCard } from '../components/ItemCard';
import { t } from '../theme';
import { useItemGroups, useStore } from '../store';

export function Inhalt() {
  const [adding, setAdding] = useState(false);
  /** Null means "everything"; tapping the active chip returns to it. */
  const [filter, setFilter] = useState<ExpiryTone | null>(null);
  const allGroups = useItemGroups();
  const view = useStore((s) => s.view);
  const setView = useStore((s) => s.setView);
  const openDetail = useStore((s) => s.openDetail);
  const startScan = useStore((s) => s.startScan);

  const list = view === 'list';

  // Filtering asks about the batch that needs attention, not the whole group.
  const groups = filter ? allGroups.filter((g) => g.soonest.expiry.tone === filter) : allGroups;

  // Counts always describe the whole fridge, never the current filter — a
  // chip that renumbered itself when tapped would be unreadable.
  const chips = [
    { label: 'heute', tone: 'urgent' as const },
    { label: 'diese Woche', tone: 'soon' as const },
    { label: 'frisch', tone: 'fresh' as const },
    // Counts are units, not products: "3 heute" must mean three things to
    // eat, not three tiles.
  ].map((c) => ({
    ...c,
    count: allGroups
      .flatMap((g) => g.batches)
      .filter((b) => b.expiry.tone === c.tone)
      .reduce((sum, b) => sum + b.qty, 0),
  }));

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', padding: '34px 38px 0' }}>
      {/* One row, not three. The old header spent ~150px of an 806px panel on a
          date already shown on the rail and the dashboard, a 52px title, and
          three decorative counters — nearly two rows of tiles. */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 18, flex: 'none' }}>
        <div
          style={{
            fontSize: 34,
            fontWeight: 500,
            letterSpacing: '-0.035em',
            whiteSpace: 'nowrap',
            flex: 'none',
          }}
        >
          Im Kühlschrank
        </div>

        {/* The counts now filter, so the space they take buys something. */}
        <div style={{ display: 'flex', gap: 8, flex: 1, minWidth: 0, overflow: 'hidden' }}>
          {chips.map((c) => {
            const active = filter === c.tone;
            return (
              <div
                key={c.label}
                className="press"
                onClick={() => setFilter(active ? null : c.tone)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: '8px 14px',
                  borderRadius: 999,
                  background: active ? 'oklch(0.32 0.012 60)' : 'oklch(0.235 0.012 60 / 0.8)',
                  border: active ? `1px solid ${TONE_OKLCH[c.tone]}` : '1px solid transparent',
                  boxShadow: 'inset 0 1px 0 oklch(1 0 0 / 0.06)',
                  whiteSpace: 'nowrap',
                  flex: 'none',
                  opacity: c.count === 0 && !active ? 0.45 : 1,
                }}
              >
                <div style={{ width: 9, height: 9, borderRadius: 999, background: TONE_OKLCH[c.tone] }} />
                <span style={{ fontSize: 17, fontWeight: 600 }}>{c.count}</span>
                <span style={{ fontSize: 16, opacity: 0.75 }}>{c.label}</span>
              </div>
            );
          })}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 'none' }}>
          <div
            className="press"
            onClick={() => setAdding(true)}
            title="Ohne Barcode einräumen"
            style={{
              width: 44,
              height: 44,
              borderRadius: 14,
              background: t.control,
              color: t.ink,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <IconPlus size={22} />
          </div>

          {/* Icons rather than the words "Kacheln"/"Liste": the shapes say it
              at a glance and cost a third of the width. */}
          <div style={{ display: 'flex', padding: 3, gap: 3, background: 'oklch(0.23 0.012 60)', borderRadius: 14 }}>
            {([
              { mode: 'tiles' as const, Icon: IconTiles, label: 'Kacheln' },
              { mode: 'list' as const, Icon: IconList, label: 'Liste' },
            ]).map(({ mode, Icon, label }) => (
              <div
                key={mode}
                className="press"
                onClick={() => setView(mode)}
                title={label}
                aria-label={label}
                style={{
                  width: 40,
                  height: 38,
                  borderRadius: 11,
                  background: view === mode ? t.controlHi : 'transparent',
                  color: view === mode ? t.ink : 'oklch(0.62 0.015 70)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Icon size={21} />
              </div>
            ))}
          </div>
        </div>
      </div>

      {groups.length === 0 && filter ? (
        /* An empty *filter* is not an empty fridge — saying so would be a lie,
           and would hide the way back. */
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 18 }}>
          <div style={{ fontSize: 28, fontWeight: 500, letterSpacing: '-0.02em', color: t.inkMuted }}>
            Nichts in dieser Gruppe
          </div>
          <div
            className="press"
            onClick={() => setFilter(null)}
            style={{ height: 60, padding: '0 28px', borderRadius: 18, border: t.hairlineStrong, display: 'flex', alignItems: 'center', fontSize: 20, fontWeight: 500, color: 'oklch(0.85 0.015 70)' }}
          >
            Alle anzeigen
          </div>
        </div>
      ) : groups.length === 0 ? (
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 20, paddingBottom: 40 }}>
          <div style={{ width: 108, height: 108, borderRadius: 28, border: '2px dashed oklch(1 0 0 / 0.18)' }} />
          <div style={{ fontSize: 34, fontWeight: 600, letterSpacing: '-0.02em' }}>Der Kühlschrank ist leer</div>
          <div style={{ fontSize: 21, color: t.inkMuted, maxWidth: 460, textAlign: 'center', textWrap: 'pretty' }}>
            Scanne einen Barcode — oder tippe ein, was du einräumst.
          </div>
          <div style={{ display: 'flex', gap: 12, marginTop: 8 }}>
            <div
              className="press"
              onClick={startScan}
              style={{ height: 78, padding: '0 34px', borderRadius: 20, background: t.accent, color: t.accentInk, display: 'flex', alignItems: 'center', fontSize: 24, fontWeight: 600 }}
            >
              Einräumen starten
            </div>
            <div
              className="press"
              onClick={() => setAdding(true)}
              style={{ height: 78, padding: '0 34px', borderRadius: 20, border: t.hairlineStrong, display: 'flex', alignItems: 'center', fontSize: 24, fontWeight: 500, color: 'oklch(0.85 0.015 70)' }}
            >
              Ohne Barcode
            </div>
          </div>
        </div>
      ) : (
        <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', marginTop: 18, paddingBottom: 30 }}>
          <div
            style={{
              display: 'grid',
              gap: 14,
              gridTemplateColumns: list
                ? 'repeat(auto-fill, minmax(420px, 1fr))'
                : 'repeat(auto-fill, minmax(196px, 1fr))',
            }}
          >
            {groups.map((g) => (
              <ItemCard key={g.key} group={g} list={list} onTap={() => openDetail(g.soonest.id)} />
            ))}
          </div>
        </div>
      )}

      {adding && <AddItem onClose={() => setAdding(false)} />}
    </div>
  );
}
